// Client-side monotonic freshness floor for the signed abax-ui manifest.
//
// The signed trust root proves a manifest is authentic and integral, but NOT
// fresh: an OLDER validly-signed manifest re-served at the stable pointer — an
// accidental re-run of an old pipeline's deploy job, or an origin rollback —
// verifies exactly as well as the current one. This helper records the highest
// CI pipeline IID the client has successfully verified and lets the loader
// reject a lower one. It is forward-only, so it never freezes independent
// abax-ui deploys: every new deploy carries a higher IID and advances the floor.
//
// Scope: a best-effort, per-device guard against the accidental-rollback footgun
// for returning clients — NOT a defense against a determined attacker (a fresh
// browser or cleared storage has no floor). An authoritative, device-independent
// floor belongs server-side (shell-backend) and is tracked separately.
//
// Persisted in localStorage with an in-memory mirror so private-mode / quota
// failures degrade to a single-session floor instead of throwing (mirrors
// services/auth/session-expiry.ts).

const STORAGE_KEY = 'abax-shell-abax-ui-pipeline-floor';

let memoryFloor: number | null = null;

function isValidFloor(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * The highest verified pipeline IID this client has loaded, or null when none is
 * recorded (fresh browser / cleared storage / private mode).
 */
export function readPipelineFloor(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw != null) {
      const parsed = Number.parseInt(raw, 10);
      if (isValidFloor(parsed)) return parsed;
    }
  } catch {
    // Storage unreadable — fall through to the in-memory mirror.
  }
  return memoryFloor;
}

/**
 * Advance the floor to `max(current, iid)`. Called only after a manifest's
 * signature verifies, so `iid` is trusted. Never lowers the floor.
 */
export function recordPipelineFloor(iid: number): void {
  if (!isValidFloor(iid)) return;
  const next = Math.max(readPipelineFloor() ?? 0, iid);
  memoryFloor = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Storage unwritable — the in-memory mirror keeps the floor for this session.
  }
}

/** Test-only: drop the persisted + in-memory floor between cases. */
// eslint-disable-next-line @typescript-eslint/naming-convention -- matches the repo's __internal test-seam convention.
export function __resetPipelineFloorForTests(): void {
  memoryFloor = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing persisted / restricted context — the mirror is already cleared.
  }
}
